import styled from 'styled-components';
import { Button, Steps, Checkbox, DatePicker as AntDatePicker, Input, Radio, Select, Typography } from 'antd';
import { colors } from './constants';

const { Title, Text, Paragraph } = Typography;

// Styled components with improved mobile responsiveness
export const WizardContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px 24px 24px;
  background: ${colors.white};
  border-radius: 12px;
  
  @media (max-width: 768px) {
    padding: 24px 16px 16px 16px;
    border-radius: 8px;
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;

export const WizardHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
  padding-bottom: 8px;
`;

export const MainTitle = styled(Title)`
  color: ${colors.text} !important;
  margin-bottom: 0 !important;
  text-align: center !important;
  font-size: 32px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  letter-spacing: -0.5px !important;
  
  @media (max-width: 768px) {
    font-size: 28px !important;
    letter-spacing: -0.3px !important;
  }
`;

export const StepContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    margin-top: 20px;
    margin-bottom: 24px;
  }
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.isLastStep ? 'column-reverse' : 'row'};
    
    button {
      margin-top: ${props => props.isLastStep ? '10px' : '0'};
      width: ${props => props.isLastStep ? '100%' : 'auto'};
    }
  }
`;

export const FormItem = styled.div`
  margin-bottom: 28px;
  
  &:last-child {
    margin-bottom: 12px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 10px;
    }
  }
`;

export const ErrorText = styled.div`
  color: ${colors.error};
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 4px;
  font-weight: 500;
  line-height: 1.4;
  min-height: 20px;
  display: block;
`;

export const StepTitle = styled(Title)`
  margin-bottom: 16px !important;
  color: ${colors.primary} !important;
  font-weight: 600 !important;
  font-size: 24px !important;
  line-height: 1.3 !important;
  
  @media (max-width: 768px) {
    font-size: 22px !important;
    margin-bottom: 14px !important;
  }
`;

export const SectionTitle = styled(Text)`
  font-weight: 600 !important;
  color: ${colors.text} !important;
  font-size: 16px !important;
  line-height: 1.4 !important;
  display: block !important;
  margin-bottom: 8px !important;
`;

export const SectionDescription = styled(Paragraph)`
  font-size: 14px !important;
  color: ${colors.text} !important;
  margin-top: 6px !important;
  margin-bottom: 14px !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
`;

export const FormSection = styled.div`
  background: ${colors.background};
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  border: 2px solid ${colors.accent};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 18px 20px;
    margin-bottom: 18px;
    border-width: 1px;
    border-radius: 10px;
  }
`;

export const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;
`;

export const StyledSteps = styled(Steps)`
  display: none !important; /* Hide completely to match bus form */
  visibility: hidden;
  height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
`;

export const StyledButton = styled(Button)`
  &.ant-btn-primary {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    border-radius: 25px;
    height: 45px;
    padding: 0 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover, &:focus {
      background-color: ${colors.secondary};
      border-color: ${colors.secondary};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.ant-btn-default {
    border-color: ${colors.primary};
    color: ${colors.primary};
    border-radius: 25px;
    height: 45px;
    padding: 0 24px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover, &:focus {
      border-color: ${colors.secondary};
      color: ${colors.secondary};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

export const InfoBox = styled.div`
  background-color: ${colors.accent};
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  border-left: 4px solid ${colors.primary};
  
  .icon {
    color: ${colors.primary};
    font-size: 18px;
    margin-right: 12px;
    margin-top: 1px;
    flex-shrink: 0;
  }
  
  .content {
    flex: 1;
    
    p {
      margin: 0 !important;
      font-size: 15px !important;
      line-height: 1.5 !important;
      color: ${colors.text} !important;
    }
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    margin-bottom: 18px;
    
    .content p {
      font-size: 14px !important;
    }
  }
`;

export const FormProgress = styled.div`
  display: none; /* Hide on desktop */
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 12px 16px;
    background: ${colors.accent};
    border-radius: 8px;
    border-left: 4px solid ${colors.primary};
    
    .title {
      font-size: 14px;
      color: ${colors.text};
      font-weight: 500;
    }
    
    .steps {
      margin-top: 4px;
      font-size: 16px;
      color: ${colors.primary};
      font-weight: 600;
      line-height: 1.3;
    }
  }
`;

// Add custom sizes for inputs and form controls - same as BusDataFormWizard
export const LargeInput = styled(Input)`
  height: 48px;
  font-size: 16px;
  padding: 8px 16px;
`;

export const LargeSelect = styled(Select)`
  .ant-select-selector {
    height: 48px !important;
    padding: 8px 16px !important;
    
    .ant-select-selection-item {
      line-height: 32px !important;
      font-size: 16px;
      color: ${colors.text} !important;
    }
    
    .ant-select-selection-placeholder {
      color: #999 !important;
      font-size: 16px;
    }
  }
  
  &.ant-select-open .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

// Global styles for Select dropdown
export const GlobalSelectStyles = styled.div`
  .ant-select-dropdown {
    .ant-select-item {
      color: ${colors.text} !important;
      background-color: white !important;
      padding: 12px 16px !important;
      font-size: 16px !important;
      
      &:hover {
        background-color: rgba(74, 144, 226, 0.1) !important;
        color: ${colors.text} !important;
      }
      
      &.ant-select-item-option-selected {
        background-color: ${colors.primary} !important;
        color: white !important;
        font-weight: 600;
        
        &:hover {
          background-color: ${colors.secondary} !important;
          color: white !important;
        }
      }
    }
  }
  
  /* Fix for date picker calendar popup text visibility */
  .ant-picker-dropdown {
    .ant-picker-panel {
      background: white !important;
      
      .ant-picker-header {
        border-bottom: 1px solid #f0f0f0;
        
        button {
          color: ${colors.text} !important;
          
          &:hover {
            color: ${colors.primary} !important;
          }
        }
      }
      
      .ant-picker-content {
        th {
          color: ${colors.text} !important;
        }
        
        td {
          .ant-picker-cell-inner {
            color: ${colors.text} !important;
            background: white !important;
            
            &:hover {
              background-color: rgba(74, 144, 226, 0.1) !important;
              color: ${colors.text} !important;
            }
          }
          
          &.ant-picker-cell-selected .ant-picker-cell-inner {
            background-color: ${colors.primary} !important;
            color: white !important;
          }
          
          &.ant-picker-cell-today .ant-picker-cell-inner {
            border-color: ${colors.primary} !important;
            color: ${colors.text} !important;
          }
          
          &.ant-picker-cell-disabled .ant-picker-cell-inner {
            color: #d9d9d9 !important;
            background: transparent !important;
          }
        }
      }
    }
  }
`;

export const LargeDatePicker = styled(AntDatePicker)`
  height: 48px;
  font-size: 16px;
  
  input {
    font-size: 16px;
    padding: 8px 16px;
    color: ${colors.text} !important;
    background: white !important;
    
    &::placeholder {
      color: #999 !important;
    }
  }
  
  .ant-picker-suffix {
    color: ${colors.text} !important;
  }
  
  &.ant-picker-focused {
    border-color: ${colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

// Missing styled components
export const LargeCheckbox = styled(Checkbox)`
  .ant-checkbox {
    transform: scale(1.3);
    margin-right: 4px;
  }
  
  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
    border-width: 2px;
  }
  
  .ant-checkbox-checked .ant-checkbox-inner::after {
    transform: rotate(45deg) scale(1) translate(-50%, -60%);
  }
  
  .ant-checkbox + span {
    font-size: 16px;
    padding-left: 12px;
    padding-right: 8px;
    line-height: 1.6;
  }
  
  &:hover .ant-checkbox-inner {
    border-color: ${colors.primary};
  }
`;

export const LargeRadio = styled(Radio)`
  .ant-radio + span {
    font-size: 16px;
    padding-right: 8px;
    padding-left: 12px;
  }
`;
